import {
    PencilIcon,
    TrashIcon,
    UserPlusIcon
} from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from 'react-redux';
import {
    useNavigate,
} from "react-router-dom";
import Button from "../../components/Button";
import FilteredTable from "../../components/FilteredTable";
import { LoadingMask } from '../../components/LoadingMask';
import { NewUserPath, NewAutomationPath, UpdateUserPath, UpdateAutomationPath } from '../../constants/Routes';
import { RoleLabel } from "../../enums/Role";
import { fetchUsers } from "../../state/users/actions";
import { Modal } from "../../components/Modal";
import { FrequencyAutomation } from "./FrequencyAutomation";
import { AppDispatch, RootState } from "../../state/store";
import { DeleteFrequencyAutomationModal } from "./DeleteFrequencyAutomationModal";
import { FrequencyFormModel } from "../../models/FollowUp";
import { toast, ToastContainer } from "react-toastify";
import { Toast } from "../../components/Toast";
import { deleteFollowUpMessage } from "../../state/automations/action";
import { longLocalDateFormat } from "../../utils/functions";

export const Automations = () => {
    const { t } = useTranslation()
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    //const automations = useSelector((state: any) => state.automations.automations);
    //const isLoading = useSelector((state: any) => state.automations.isLoading);
    const folloUpMessages = useSelector((state: RootState) => state.automations.followUpMessages)
    const isLoadingFollowUpMessages = useSelector((state: RootState) => state.automations.isLoadingFollowUpMessages);
    const errorFollowUpMessages = useSelector((state: any) => state.automations.errorFollowUpMessages);
    const [showFollowUpError, setShowFollowUpError] = useState(false);

    const [frequencyAutomationModal, setFrequencyAutomationModal] = useState<{
        show: boolean;
        update: boolean;
        followUp?: FrequencyFormModel;
        index?: number;
    }>({ show: false, update: false });

    const [ showDeleteFrequencyAutomationModal, setShowDeleteFrequencyAutomationModal] = useState<{
        followUp?: FrequencyFormModel;
        show: boolean;
    }>({ show: false, followUp: undefined });

    useEffect(() => {
        if (showFollowUpError) {
            Toast(
                t(errorFollowUpMessages?.reason ?? "SOMETHING_WENT_WRONG"),
                t(errorFollowUpMessages?.cause_info)
            );
        }
    }, [showFollowUpError, errorFollowUpMessages, t]);

    const columns = useMemo(
        () => [
            {
                Header: t('DESCRIPTION'),
                accessor: "description",
            },
            {
                Header: t('FREQUENCY'),
                Cell: ( row: any ) => {
                    const { frequency } = row.row.original;
                    return frequency ? frequency : "-";
                },
            },
            {
                Header: t('FREQUENCY_UNIT'),
                Cell: ( row: any ) => {
                    const { frequency_unit } = row.row.original;
                    return frequency_unit ? frequency_unit === "day" ? t("DAY") : t("MONTH") : "-";
                },
            },
            {
                Header: t('FREQUENCY_TIME'),
                Cell: ( row: any ) => {
                    const { frequency_time } = row.row.original;
                    return frequency_time ? frequency_time : "-";
                },
            },
            {
                Header: t('SPECIFIC_DATE'),
                Cell: ( row: any ) => {
                    const { specific_date } = row.row.original;
                    return specific_date ? longLocalDateFormat(specific_date) : "-";
                },
            },
            {
                Header: t('SPECIFIC_DATE_RECURRENT'),
                Cell: ( row: any ) => {
                    const { is_specific_date_recurrent } = row.row.original;
                    return is_specific_date_recurrent ? is_specific_date_recurrent === '0' ? t("NO") : t("YES") : "-";
                },
            },
            {
                id: "actions",
                Header: "",
                Cell: (row: any) => {
                    return (
                        <div className="flex flex-row justify-center divide-x">
                            <Button
                                isTerciary
                                iconOnly
                                tooltip={t("UPDATE_AUTOMATION")}
                                onClick={() => setFrequencyAutomationModal({ show: true, update: true, followUp: row.row.original, index: row.row.index })}
                                classNames="mr-2"
                            >
                                <PencilIcon className="h-5 aspect-square text-blue" />
                            </Button>
                            <Button
                                isTerciary
                                iconOnly
                                tooltip={t("DELETE_AUTOMATION")}
                                onClick={() => setShowDeleteFrequencyAutomationModal({ show: true, followUp: row.row.original })}
                                classNames="mr-2"
                            >
                                <TrashIcon className="h-5 aspect-square text-red-600" />
                            </Button>
                        </div>
                    );
                },
            }
        ],
        [t]
    );

    const updateAutomation = (row: any) => {
        navigate(UpdateAutomationPath, { state: { automation: row } });
    };

    const removeAutomation = (followUp: FrequencyFormModel) => {
        //TODO: remove address from database
        dispatch(deleteFollowUpMessage({ followUpId: followUp.id! })).then((e: any) => {
            if (e.type === "followUp/deleteFollowUpMessage/rejected") {
                setShowFollowUpError(true);
            } else {
                toast(t("SUCCESSFULLY_DELETED_FOLLOW_UP"));
            }
        });
    }

    return (<>
        {isLoadingFollowUpMessages && <LoadingMask />}
        <ToastContainer progressStyle={{ background: "#D4AF37" }} />
        <FilteredTable
            columns={columns}
            data={folloUpMessages}
            buttonAction={<Button onClick={() => {
                setFrequencyAutomationModal({ show: true, update: false, followUp: undefined, index: undefined})
            }}>
                <span className='flex items-center font-semibold pr-3'>
                    <div className='w-8 p-1 aspect-square mr-2'><UserPlusIcon /></div>
                    {t('NEW_AUTOMATION')}
                </span>
            </Button>}
        />
        <FrequencyAutomation
            info={frequencyAutomationModal}
            onClose={() => setFrequencyAutomationModal({ show: false, update: false, followUp: undefined, index: undefined })}
        />
        <DeleteFrequencyAutomationModal
            info={showDeleteFrequencyAutomationModal}
            onClose={() => setShowDeleteFrequencyAutomationModal({show : false, followUp: undefined})}
            deleteAutomation={(followUp: FrequencyFormModel) => removeAutomation(followUp)}
        />
    </>
    )
}